import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Box, Button, Flex, Heading, Input, Select, Text, Table, Thead, Tbody, Tr, Th, Td, Spinner } from '@chakra-ui/react';
import { agentGet } from '../../utils/apis/agentApi';

type Review = { id: string; source: string; name: string; phone?: string; role: string; rating: number | null; comment?: string; provider?: string; service?: string; created_at: string; disabled?: boolean };
type Results = { items: Review[]; total: number; average: number | null };
const sources: Record<string, string> = { chat: 'Agent experience', intro: 'Provider introduction', job: 'Dispatch job', platform: 'App / website review' };

export default function Ratings() {
  const [params, setParams] = useSearchParams();
  const provider = params.get('provider') || '';
  const [source, setSource] = useState('all');
  const [rating, setRating] = useState('');
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [data, setData] = useState<Results | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    const params = { source, rating: rating || undefined, q: search, page, limit: 50, provider_id: provider || undefined };
    const request = source === 'platform'
      ? axios.get<Results>(`${process.env.REACT_APP_BASE_URL}/admin/ratings-reviews`, { params, headers: { Authorization: `Bearer ${localStorage.getItem('AuthToken')}` } }).then(r => ({ok: true, data: r.data})).catch(() => ({ok: false, data: null}))
      : agentGet<Results>('api/ratings', params);
    request.then(res => {
      if (cancelled) return;
      if (res.ok && res.data) setData(res.data);
      else { setData(null); setError('Could not load ratings. Please retry.'); }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [source, rating, search, page, reload, provider]);
  return <Box p={5}>
    <Flex justify="space-between" gap={3} mb={3}><Heading size="md">Ratings &amp; Reviews</Heading>
      <Button onClick={() => setReload(n => n + 1)}>Refresh</Button></Flex>
    <Text mb={4}>Feedback from customers and service providers. Choose App / website review to see platform reviews, including reviews synced from the agent. Agent experience scores rate the conversation; service reviews rate the work.</Text>
    {provider && <Flex gap={3} align="center" mb={4}><Text>Service reviews for provider {provider}</Text><Button size="sm" onClick={() => {setParams({}); setPage(1);}}>Show all reviews</Button><Button as={Link} size="sm" to="/provider-performance">Provider performance</Button></Flex>}
    <Flex as="form" gap={3} wrap="wrap" mb={4} onSubmit={(e: React.FormEvent) => { e.preventDefault(); setSearch(query); setPage(1); }}>
      <Select aria-label="Review type" maxW="240px" value={source} onChange={e => { setSource(e.target.value); setPage(1); }}>
        <option value="all">All agent feedback</option>{Object.entries(sources).filter(([key]) => !provider || ['intro', 'job'].includes(key)).map(([key, name]) => <option key={key} value={key}>{name}</option>)}
      </Select>
      <Select aria-label="Stars" maxW="150px" value={rating} onChange={e => { setRating(e.target.value); setPage(1); }}>
        <option value="">All stars</option>{[5,4,3,2,1].map(n => <option key={n} value={n}>{n} stars</option>)}
      </Select>
      <Input aria-label="Search reviews" maxW="340px" placeholder="Name, phone, service or comment" value={query} onChange={e => setQuery(e.target.value)} />
      <Button type="submit">Search</Button>
    </Flex>
    {loading ? <Spinner aria-label="Loading ratings" /> : error ? <Box role="alert"><Text>{error}</Text><Button onClick={() => setReload(n => n + 1)}>Retry</Button></Box> : data && <>
      <Text mb={3}>{data.total} reviews{source !== 'all' && data.average != null ? ` · Average ${Number(data.average).toFixed(1)} / 5` : ''}</Text>
      {!data.items.length ? <Text>No reviews match these filters.</Text> : <Box overflowX="auto"><Table size="sm">
        <Thead><Tr>{['Date', 'Type', 'From', 'Rating', 'Comment', 'Service / reviewed person', ''].map((title, i) => <Th key={i}>{title}</Th>)}</Tr></Thead>
        <Tbody>{data.items.map(r => <Tr key={r.id}>
          <Td whiteSpace="nowrap">{new Date(r.created_at).toLocaleString()}</Td><Td>{sources[r.source]}{r.disabled && <Text fontSize="xs">Hidden from public view</Text>}</Td>
          <Td>{r.name}<Text fontSize="xs">{r.role}{r.phone && r.phone !== r.name ? ` · ${r.phone}` : ''}</Text></Td>
          <Td whiteSpace="nowrap">{r.rating == null ? 'Not rated' : `${'★'.repeat(r.rating)} (${r.rating}/5)`}</Td>
          <Td minW="200px" maxW="420px" whiteSpace="pre-wrap" overflowWrap="anywhere">{r.comment || 'No written comment'}</Td>
          <Td>{r.service || '—'}{r.provider && <Text>{r.provider}</Text>}</Td>
          <Td>{r.phone && <Button as={Link} size="sm" variant="link" colorScheme="purple" to={`/conversations?phone=${encodeURIComponent(r.phone)}`}>View conversation</Button>}</Td>
        </Tr>)}</Tbody>
      </Table></Box>}
      <Flex gap={3} mt={4} align="center"><Button isDisabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
        <Text>Page {page} of {Math.max(1, Math.ceil(data.total / 50))}</Text>
        <Button isDisabled={page * 50 >= data.total} onClick={() => setPage(p => p + 1)}>Next</Button></Flex>
    </>}
  </Box>;
}
